import React, { useEffect, useState } from "react"
import { SimpleForm, 
    TextInput, 
    ReferenceInput,
    SelectInput,
    AutocompleteInput,
    SaveButton, 
    DeleteButton, 
    required, 
    useDataProvider } from "react-admin"
import { EquipmentKindEntity } from "api/equipment/equipment.pb"
import { ScreenEmpty } from "components/ui/screen"
import { useTranslate } from "ra-core"
import { Grid } from "@mui/material"
import { NIL as UUID_NIL } from 'uuid'
import { useController } from "react-hook-form"
import { TextInputWithAudit, AutocompleteInputWithAudit, SelectInputWithAudit } from "components/audit/audit.inputs"

interface props {
    readonly: boolean
    newRecord: boolean
}

const TableName = "equipments"

function Form(props: React.PropsWithChildren<props>): JSX.Element  {
    const { readonly, newRecord } = props
    const translate = useTranslate()
    const dataProvider = useDataProvider()
    const [companyType, setCompanyType] = React.useState(UUID_NIL)

    const [equipmentkinds, setEquipmentKinds] = useState<EquipmentKindEntity[]>([])
    useEffect(() => {
        dataProvider.getList("equipment-kinds", {})
            .then(({ data }) => setEquipmentKinds(data))
            .catch(() => setEquipmentKinds([]))
    }, [dataProvider])

    useEffect(() => {
        dataProvider
            .getList("company-types", {
                filter: { varname: "equipment_producer" },
                pagination: { page: 1, perPage: 1 },
            })
            .then(({ data }) => {
                if (data.length > 0) {
                    setCompanyType(data[0].id)
                }
            })
    }, [])

    const getEquipmentKindVarnameById = (id: string): string => {
        const kind = equipmentkinds.find(k => k.id === id)
        return kind?.varname || ""
    }

    const SensorBorders = (): JSX.Element => {
        const { field: { value: selectedEquipmentKind } } = useController({ name: 'equipment_kind_id' })
        const kindVarname = getEquipmentKindVarnameById(selectedEquipmentKind)
        let unitChoses: { id: string; name: string }[] = []
        //TODO:
        switch (kindVarname) {
            case "sensor_p":
                unitChoses = [
                     { id: 'kpa', name: 'кПа' },
                     { id: 'mpa', name: 'МПа' },
                ]
                break
            case "sensor_t":
                unitChoses = [
                     { id: 'c', name: 'C' },
                     { id: 'f', name: 'F' },
                ]
                break
        }

        if ((kindVarname !== "sensor_p" && kindVarname !== "sensor_t") || equipmentkinds.length === 0) {
            return <></>
        }
        
        
        return (
            <Grid container columnSpacing="8">
                <Grid item xs={2}>
                    <TextInputWithAudit 
                        tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly}
                        source="props[min]"
                        type="text"
                        inputProps={{ inputMode: "decimal", pattern: "-?[0-9]*[.,]?[0-9]*" }}
                        label={translate("resources.equipments.fields.sensor_min")}
                        validate={[
                            required(),
                            (value: any) => {
                                // Разрешаем отрицательные числа, точки и запятые
                                if (typeof value === 'string' && value.trim() !== '' && /^-?[0-9]+([.,][0-9]*)?$/.test(value.trim())) {
                                    return undefined
                                }
                                return "Введите число"
                            }
                        ]}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextInputWithAudit 
                        tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly}
                        source="props[max]"
                        type="text"
                        inputProps={{ inputMode: "decimal", pattern: "-?[0-9]*[.,]?[0-9]*" }}
                        label={translate("resources.equipments.fields.sensor_max")}
                        validate={[
                            required(),
                            (value: any) => (isNaN(parseFloat(value)) ? "Введите число" : undefined)
                        ]}
                    />
                </Grid>
                <Grid item xs={2}>
                    <SelectInputWithAudit 
                        tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly}
                        source="props[unit]"
                        validate={[required()]}
                        label={translate("resources.equipments.fields.sensor_unit")}
                        choices={unitChoses}
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <SimpleForm toolbar={false}>
            <Grid container >
                <Grid item xs={6}>
                    <TextInputWithAudit tableName={TableName}
                            disabled={readonly}
                            showAuditIcon={readonly} source="name" validate={[required()]} label={translate("resources.equipments.fields.name")}/>
                </Grid>
            </Grid>
            <Grid container columnSpacing="8">
                <Grid item xs={3}>
                    <SelectInputWithAudit tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly} source="equipment_kind_id"  label={translate("resources.equipments.fields.equipment_kind_id")} optionText="name" validate={[required()]} choices={equipmentkinds}/>
                </Grid>
                <Grid item xs={3}>
                    <ReferenceInput  label={translate("resources.equipments.fields.equipment_driver_id")} source="equipment_driver_id" reference="equipment-drivers">
                        <SelectInputWithAudit tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly} optionText="name"  />
                    </ReferenceInput>
                </Grid>
            </Grid>
            <SensorBorders />

            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <ReferenceInput  
                        label={translate("resources.equipments.fields.manufacturer_company_id")} 
                        source="manufacturer_company_id" 
                        reference="companies"
                        filterToQuery={(searchText: string) => ({ name: searchText })}
                        filter={{ type_id: companyType }}>
                        <AutocompleteInputWithAudit tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly} optionText="name" />
                    </ReferenceInput>
                </Grid>
                <Grid item xs={3}>
                    <ReferenceInput label={translate("resources.equipments.fields.energy_kind_id")} source="energy_kind_id" reference="energy-kinds">
                        <SelectInputWithAudit tableName={TableName}
                        disabled={readonly}
                        showAuditIcon={readonly} optionText="name" />
                    </ReferenceInput>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={7}>
                    <TextInputWithAudit tableName={TableName}
                            disabled={readonly}
                            showAuditIcon={readonly} source="description" multiline rows={5} label={translate("resources.common.fields.description")}/>
                </Grid>
            </Grid>
            <ScreenEmpty enabled={!readonly}>
                <Grid container>
                    <Grid item xs={6}>
                        <SaveButton />
                    </Grid>
                    <ScreenEmpty enabled={!newRecord}>
                        <Grid item xs={6}>
                            <DeleteButton />
                        </Grid>
                    </ScreenEmpty>
                </Grid>
            </ScreenEmpty>
        </SimpleForm>
    )
}

Form.defaultProps = {
    readonly: false,
    newRecord: false,
}

export default Form